import { useTranslation } from "react-i18next";
import { Mail, Phone } from "lucide-react";

const teamImages = {
  annika: {
    src: "/assets/team/annika-800.webp",
    webpSrcSet: "/assets/team/annika-480.webp 480w, /assets/team/annika-800.webp 800w",
    avifSrcSet: "/assets/team/annika-480.avif 480w, /assets/team/annika-800.avif 800w",
  },
  kristin: {
    src: "/assets/team/kristin-800.webp",
    webpSrcSet: "/assets/team/kristin-480.webp 480w, /assets/team/kristin-800.webp 800w",
    avifSrcSet: "/assets/team/kristin-480.avif 480w, /assets/team/kristin-800.avif 800w",
  },
  marju: {
    src: "/assets/team/marju-800.webp",
    webpSrcSet: "/assets/team/marju-480.webp 480w, /assets/team/marju-800.webp 800w",
    avifSrcSet: "/assets/team/marju-480.avif 480w, /assets/team/marju-800.avif 800w",
  },
};

interface TeamProps {
  sectionId?: string | null;
}

export function Team({ sectionId = "team" }: TeamProps) {
  const { t } = useTranslation();

  const teamMembers = [
    {
      name: "Annika Pillerpau",
      image: teamImages.annika,
      role: t('team.members.annika.role'),
      description: t('team.members.annika.description'),
      email: "annika@4sistersevents.ee",
      phone: "+372 51 03 954"
    },
    {
      name: "Kristin Kurtna",
      image: teamImages.kristin,
      role: t('team.members.kristin.role'),
      description: t('team.members.kristin.description'),
      email: "kristin@4sistersevents.ee",
      phone: "+372 51 92 3590"
    },
    {
      name: "Marju Heinla",
      image: teamImages.marju,
      role: t('team.members.marju.role'),
      description: t('team.members.marju.description'),
      email: "marju@4sistersevents.ee",
      phone: "+372 55 53 6850"
    }
  ];

  return (
    <section id={sectionId ?? undefined} className="py-20 bg-gray-50" data-testid="team-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="font-poppins text-4xl md:text-5xl font-bold text-dark mb-4">
            {t('team.title')} <span className="text-primary">{t('team.titleHighlight')}</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            {t('team.subtitle')}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-12 lg:gap-16">
          {teamMembers.map((member) => (
            <div 
              key={member.name}
              className="group relative bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2"
              data-testid={`team-member-${member.name.split(' ')[0].toLowerCase()}`}
            >
              <div className="aspect-[4/5] overflow-hidden">
                <picture className="block h-full w-full">
                  <source
                    srcSet={member.image.avifSrcSet}
                    sizes="(min-width: 768px) 33vw, 100vw"
                    type="image/avif"
                  />
                  <source
                    srcSet={member.image.webpSrcSet}
                    sizes="(min-width: 768px) 33vw, 100vw"
                    type="image/webp"
                  />
                  <img
                    src={member.image.src}
                    srcSet={member.image.webpSrcSet}
                    sizes="(min-width: 768px) 33vw, 100vw"
                    alt={`${member.name} - ${member.role}`}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                    loading="lazy"
                    decoding="async"
                    width="800"
                    height="534"
                  />
                </picture>
              </div>
              <div className="p-8 text-center">
                <h3 className="font-poppins text-2xl font-bold text-dark mb-2">
                  {member.name}
                </h3>
                <p className="text-primary font-semibold mb-4">
                  {member.role}
                </p>
                <p className="text-gray-600 leading-relaxed mb-4">
                  {member.description}
                </p>
                <div className="flex flex-col items-center gap-2 text-sm text-gray-600">
                  <a 
                    href={`mailto:${member.email}`} 
                    className="flex items-center gap-2 hover:text-primary transition-colors"
                  >
                    <Mail className="w-4 h-4" />
                    {member.email}
                  </a>
                  <a 
                    href={`tel:${member.phone.replace(/\s/g, '')}`} 
                    className="flex items-center gap-2 hover:text-primary transition-colors"
                  >
                    <Phone className="w-4 h-4" />
                    {member.phone}
                  </a>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
